import { ApiError, type BaselineImportCounts, type ErrorDetail } from '../../api';
import type { Column } from '../../components';
import { formatInteger } from '../../format';

/**
 * What the baseline import screen says, and how it turns the API's refusals
 * into something an analyst can act on without leaving the page.
 *
 * The import is all-or-nothing, which puts the whole weight of the feature on
 * this file. Somebody pastes two thousand people out of a payroll system, four
 * rows are wrong, and the answer has to be "line 418, the CPF; line 903, the
 * birth date" rather than "the file was rejected" -- because the next thing
 * they do is open the CSV and fix exactly those lines.
 *
 * Three refusals arrive by three different shapes and are read three different
 * ways, which is the reason this module exists rather than a `messageForCode`
 * call in the screen:
 *
 *  - **422 with `details`** -- one entry per offending row. `field` is the row
 *    location the server built (`row 4.cpf`), which is the machine-readable
 *    half of the answer: the line number and the canonical column name. That is
 *    what {@link rowProblemsOf} keys on, following the same discipline as
 *    `customer/spreadsheetUpload.ts` -- branch on the stable part, never on
 *    prose that will be improved next month.
 *  - **422 without `details`** -- the file is not a baseline source at all: a
 *    missing column, a `subestipulante` column that belongs to the contract and
 *    never to a person. There is no row to point at, so it is one message.
 *  - **409** -- the contract has already moved on, and the analyst may override.
 *
 * For the last two the server's own sentence is quoted verbatim, under a label
 * that says it is the server speaking. It carries the part we structurally
 * cannot reconstruct -- *which* column is forbidden, *which* kind of movement
 * already exists and on what date -- and an analyst deciding whether to
 * override needs precisely that. Everything this product writes is pt-BR; a
 * quoted answer is data, and is marked as such.
 */

/**
 * What a baseline file input offers: a workbook or a CSV. The server tells the
 * two apart by content and refuses a pre-2007 `.xls` with what to do about it,
 * so offering only these is a convenience and not the check.
 */
export const SPREADSHEET_ACCEPT =
  '.xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const BASELINE_COPY = {
  title: 'População inicial',
  description:
    'A base que esta empresa já tem hoje na operadora, carregada de uma vez só. É o ponto de ' +
    'partida do cadastro: tudo o que vier depois é movimentação sobre ela.',
  back: 'Voltar para a empresa',

  // --- The form ------------------------------------------------------------
  formTitle: 'Importar população',
  formDescription:
    'Uma planilha (.xlsx) ou um CSV com uma linha por pessoa. Ou tudo entra, ou nada entra: ' +
    'uma linha com problema recusa o arquivo inteiro e nada é gravado.',
  fileLabel: 'Arquivo da população',
  fileHint:
    'Planilha .xlsx ou CSV com as colunas CPF, Nome, Data de nascimento e Salário. No CSV, ' +
    'separador vírgula ou ponto e vírgula. Colunas a mais são ignoradas — menos Subestipulante ' +
    'e Módulo, que são do contrato e recusam o arquivo: a planilha com vários subestipulantes ' +
    'se importa pela empresa. A coluna Início da cobertura é opcional: preenchida, é a data ' +
    'de início daquela pessoa. Arquivo .xls antigo: salve de novo como .xlsx ou .csv.',
  baselineDateLabel: 'Data de início da cobertura',
  baselineDateHint:
    'A data a partir da qual estão cobertas as pessoas cuja linha não traz a própria data na ' +
    'coluna Início da cobertura. Não é a data de hoje nem a data do arquivo: é desde quando ' +
    'esta população está no plano.',
  submit: 'Importar população',
  submitting: 'Importando…',
  missingFile: 'Escolha o arquivo da população.',
  missingDate: 'Informe a data de início da cobertura.',
  imported: 'População importada.',

  // --- Rejections ----------------------------------------------------------
  rejectedTitle: 'Nada foi importado',
  rejectedDescription:
    'A importação é tudo ou nada. Corrija as linhas abaixo no arquivo e envie de novo — ' +
    'nenhuma pessoa foi gravada.',
  rejectedCaption: 'Linhas recusadas do arquivo enviado',
  lineHeader: 'Linha',
  columnHeader: 'Coluna',
  reasonHeader: 'O que corrigir',
  formatTitle: 'O arquivo não pôde ser lido',
  formatDescription:
    'O problema é do arquivo como um todo, não de uma linha: uma coluna que falta, uma coluna ' +
    'repetida, ou uma coluna que pertence ao contrato e não à pessoa.',
  serverSaid: 'O servidor respondeu:',

  // --- The 409 and its override -------------------------------------------
  conflictTitle: 'Este contrato já se moveu',
  conflictDescription:
    'A importação foi recusada porque o cadastro deste contrato não é mais uma folha em ' +
    'branco — já há inclusão, exclusão, alteração salarial ou uma importação anterior em ' +
    'outra data. Importar mesmo assim reescreve o ponto de partida e fica registrado como ' +
    'decisão de quem importou.',
  overrideLabel: 'Sim, importar mesmo assim sobre o cadastro que já existe',
  overrideSubmit: 'Importar mesmo assim',
  overrideHint: 'Marque a caixa acima para liberar este botão.',

  // --- The history ---------------------------------------------------------
  historyTitle: 'Importações já feitas',
  historyDescription:
    'Toda importação deste contrato, da mais recente para a mais antiga, com o arquivo que a ' +
    'originou.',
  historyCaption: 'Importações de população deste contrato',
  historyLoading: 'Carregando importações…',
  historyError: 'Não foi possível carregar as importações',
  historyEmpty: 'Nenhuma importação neste contrato',
  historyEmptyDescription:
    'A população inicial ainda não foi carregada. Use o formulário acima para começar.',
  whenHeader: 'Quando',
  whoHeader: 'Quem importou',
  baselineDateHeader: 'Cobertura desde',
  rowCountHeader: 'Linhas',
  overrideHeader: 'Sobrescreveu',
  sourceHeader: 'Arquivo',
  overrideYes: 'Sim',
  overrideNo: 'Não',
  unknownUser: 'Conta removida',
  download: 'Baixar arquivo',
  downloading: 'Baixando…',
  downloadError: 'Não foi possível baixar o arquivo',
} as const;

/**
 * What the company-wide import says: the customer's own sheet, every
 * sub-contract at once. Same discipline as {@link BASELINE_COPY}, which it
 * borrows the rejection and history wording from where the meaning is the same.
 */
export const COMPANY_BASELINE_COPY = {
  action: 'Importar população inicial',
  title: 'População inicial da empresa',
  description:
    'A planilha que o cliente entrega, com as pessoas de vários subestipulantes, carregada ' +
    'de uma vez só. Cada linha vai para o contrato do subestipulante que ela informa.',
  back: 'Voltar para a empresa',

  formTitle: 'Importar planilha da empresa',
  formDescription:
    'Uma linha por pessoa, no layout de nove colunas que o cliente já usa. Ou tudo entra, ou ' +
    'nada entra — em nenhum contrato: uma linha com problema recusa a planilha inteira.',
  fileLabel: 'Planilha da população',
  fileHint:
    'Planilha .xlsx ou CSV com Subestipulante, Módulo, Nome, Data de nascimento, CPF, ' +
    'Salário, INCLUSAO, EXCLUSÃO e ALTERAÇÃO SALARIO. O Módulo em branco vale; preenchido, ' +
    'precisa ser o do contrato. Linhas marcadas em EXCLUSÃO não entram numa população ' +
    'inicial. A coluna Início da cobertura é opcional: preenchida, é a data de início daquela ' +
    'pessoa. Arquivo .xls antigo: salve de novo como .xlsx ou .csv.',
  noContracts: 'Esta empresa ainda não tem contrato',
  noContractsDescription:
    'Cadastre os contratos antes: é o subestipulante de cada um que diz para onde vai cada ' +
    'linha da planilha.',
  submit: 'Importar planilha',
  imported: 'População da empresa importada.',

  conflictTitle: 'Há contratos que já se moveram',
  conflictDescription:
    'A planilha foi recusada inteira porque o cadastro dos contratos abaixo não é mais uma ' +
    'folha em branco. Importar mesmo assim reescreve o ponto de partida deles e fica ' +
    'registrado como decisão de quem importou.',
  conflictCaption: 'Contratos que recusaram a importação',
  conflictContractHeader: 'Contrato',
  conflictReasonHeader: 'O servidor respondeu',
  overrideLabel: 'Sim, importar mesmo assim sobre os cadastros que já existem',

  resultTitle: 'O que foi carregado',
  resultDescription:
    'Cada contrato recebeu uma importação própria, que aparece no histórico dele com a ' +
    'planilha que a originou.',
  resultCaption: 'População carregada por contrato',
  contractHeader: 'Contrato',
  rowsHeader: 'Linhas lidas',
  peopleHeader: 'Pessoas cadastradas',
  coverageHeader: 'Coberturas abertas',
  salariesHeader: 'Salários registrados',
  presentHeader: 'Já estavam',
  overriddenNote: 'sobrescreveu',
  total: 'Total',
} as const;

/** A result row: one contract, or the totals beneath them. */
export interface ResultRow {
  readonly key: string;
  readonly label: string;
  readonly counts: BaselineImportCounts;
}

function count(key: keyof BaselineImportCounts, header: string): Column<ResultRow> {
  return { key, header, align: 'end', render: (row) => formatInteger(row.counts[key]) };
}

/** The counts table every sheet import answers with, one row per contract. */
export const RESULT_COLUMNS: readonly Column<ResultRow>[] = [
  { key: 'contract', header: COMPANY_BASELINE_COPY.contractHeader, render: (row) => row.label },
  count('row_count', COMPANY_BASELINE_COPY.rowsHeader),
  count('people_registered', COMPANY_BASELINE_COPY.peopleHeader),
  count('coverage_periods_opened', COMPANY_BASELINE_COPY.coverageHeader),
  count('salary_versions_recorded', COMPANY_BASELINE_COPY.salariesHeader),
  count('already_present', COMPANY_BASELINE_COPY.presentHeader),
];

/** A contract's result label, marked when the import overrode it. */
export function resultLabel(label: string, overridden: boolean): string {
  return overridden ? `${label} (${COMPANY_BASELINE_COPY.overriddenNote})` : label;
}

/**
 * What the group-wide import says: the same sheet, across every company of an
 * economic group. Borrows from {@link COMPANY_BASELINE_COPY} wherever the
 * meaning is the same, which is almost everywhere.
 */
export const GROUP_BASELINE_COPY = {
  formTitle: 'Importar população inicial do grupo',
  formDescription:
    'A planilha que o cliente entrega, com as pessoas de todas as empresas do grupo, carregada ' +
    'de uma vez só. Cada linha vai para o contrato do subestipulante que ela informa, na ' +
    'empresa do grupo que tem esse contrato. Ou tudo entra, ou nada entra — em nenhuma ' +
    'empresa: uma linha com problema recusa a planilha inteira.',
  fileHint:
    'Planilha .xlsx ou CSV com Subestipulante, Módulo, Nome, Data de nascimento, CPF, ' +
    'Salário, INCLUSAO, EXCLUSÃO e ALTERAÇÃO SALARIO. As linhas com o nome de cada empresa ' +
    'entre os blocos são ignoradas. O Módulo em branco vale; preenchido, precisa ser o do ' +
    'contrato. Linhas marcadas em EXCLUSÃO não entram numa população inicial. A coluna ' +
    'Início da cobertura é opcional: preenchida, é a data de início daquela pessoa. Arquivo ' +
    '.xls antigo: salve de novo como .xlsx ou .csv.',
  noContracts: 'Este grupo ainda não tem empresa',
  noContractsDescription:
    'Adicione as empresas em "Adicionar empresa", com os contratos delas já cadastrados: é o ' +
    'subestipulante de cada contrato que diz para onde vai cada linha da planilha.',
  imported: 'População do grupo importada.',
  conflictDescription:
    'A planilha foi recusada inteira, em todas as empresas, porque o cadastro dos contratos ' +
    'abaixo não é mais uma folha em branco. Importar mesmo assim reescreve o ponto de ' +
    'partida deles e fica registrado como decisão de quem importou.',
  conflictCompanyHeader: 'Empresa',
  resultDescription:
    'Cada contrato recebeu uma importação própria, na empresa a que pertence, que aparece no ' +
    'histórico dele com a planilha que a originou.',
  resultCaptionFor: (companyName: string) => `População carregada em ${companyName}`,
  totalCaption: 'Total do grupo',
  total: 'Total do grupo',
} as const;

/**
 * How a contract is told apart from its siblings in this company: by the
 * insurer and the two codes, which is how the customer's sheet names it too.
 */
export function contractLabel(
  subestipulante: number,
  modulo: number,
  insurerName?: string,
): string {
  const codes = `Subestipulante ${subestipulante} / módulo ${modulo}`;
  return insurerName === undefined ? codes : `${insurerName} — ${codes}`;
}

/** One contract that refused a company-wide or group-wide import, as the 409 names it. */
export interface MovedOnContract {
  readonly contractId: string;
  /** The company holding it, named only by a group's 409; null for a company's. */
  readonly companyId: string | null;
  /** The server's own sentence, quoted under a label saying so. */
  readonly sentence: string;
}

/**
 * `contracts.<uuid>`, or `companies.<uuid>.contracts.<uuid>` from a group's
 * import -- the stable half of a 409 detail.
 */
const CONTRACT_LOCATION = /^(?:companies\.([^.]+)\.)?contracts\.(.+)$/;

/**
 * The contracts a company-wide or group-wide 409 names, one per `details` entry.
 *
 * Empty for anything else -- including the per-contract 409, which names its
 * one contract in the message and carries no details.
 */
export function movedOnContractsOf(error: unknown): readonly MovedOnContract[] {
  if (!(error instanceof ApiError) || error.status !== 409) {
    return [];
  }
  return error.details
    .map((detail: ErrorDetail): MovedOnContract | null => {
      const match = CONTRACT_LOCATION.exec(detail.field ?? '');
      return match === null
        ? null
        : {
            contractId: match[2] as string,
            companyId: match[1] ?? null,
            sentence: detail.message,
          };
    })
    .filter((contract): contract is MovedOnContract => contract !== null);
}

/**
 * The canonical columns of a baseline source, in pt-BR.
 *
 * These are the names the parser normalises every accepted spelling onto, so
 * they are the stable half of a row problem and safe to key on. An unknown one
 * is rendered as it arrived rather than dropped: a column we have not met is
 * still a column the analyst has to find in their file.
 */
const COLUMN_LABELS: Record<string, string> = {
  cpf: 'CPF',
  full_name: 'Nome',
  birth_date: 'Data de nascimento',
  salary: 'Salário',
  subestipulante: 'Subestipulante',
  modulo: 'Módulo',
  termination_mark: 'EXCLUSÃO',
};

/** One offending row of the source, as the screen renders it. */
export interface RowProblem {
  /** The line in the uploaded file, counting the header as line 1. */
  readonly line: string;
  /** The column at fault, or a dash when the problem is the row as a whole. */
  readonly column: string;
  readonly reason: string;
}

/** `row 4.cpf` -> line 4, column `cpf`. `row 4` -> line 4, no column. */
const ROW_LOCATION = /^row (\d+)(?:\.(.+))?$/;

/** `is the same person as row 3; ...` -- the one problem that names another row. */
const DUPLICATE_OF_ROW = /^is the same person as row (\d+)/;

const REQUIRED_AND_EMPTY = 'is required and is empty.';

/**
 * What to tell the analyst about one column, when all we know is which column.
 *
 * The fallback, and deliberately the common case: the server's own sentence is
 * English prose written for whoever is integrating, so the pt-BR answer is
 * built from the column name -- which is part of the file format and does not
 * get reworded. Each one says what a correct cell looks like, because that is
 * the question somebody staring at a spreadsheet actually has.
 */
const REASON_BY_COLUMN: Record<string, string> = {
  cpf: 'O CPF não foi aceito. Confira os onze dígitos e os dígitos verificadores.',
  full_name: 'O nome não foi aceito. Preencha o nome completo da pessoa.',
  birth_date:
    'A data de nascimento não foi aceita. Use DD/MM/AAAA ou AAAA-MM-DD, e confira se o ano ' +
    'está certo.',
  salary: 'O salário não foi aceito. Use 4.500,00 ou 4500.00, sem texto junto.',
  subestipulante:
    'O subestipulante não foi aceito. Use o código numérico de um contrato da empresa.',
  modulo:
    'O módulo não é o do contrato deste subestipulante. Corrija, ou deixe em branco para não ' +
    'conferir.',
  termination_mark:
    'A linha está marcada em EXCLUSÃO. A população inicial lista só quem está coberto na ' +
    'data; tire a linha da planilha.',
};

/**
 * The ways a Subestipulante fails to name a contract, by the start of the
 * server's sentence -- which is stable for the same reason the duplicate one
 * is: the screen keys on it. A group's import says "of this group", and is
 * matched first: the company's patterns would match it too.
 */
const SUBESTIPULANTE_REASONS: readonly (readonly [RegExp, string])[] = [
  [
    /^matches no contract of this group/,
    'Nenhum contrato das empresas deste grupo tem este subestipulante. Confira o código, ou ' +
      'cadastre o contrato na empresa certa antes.',
  ],
  [
    /^matches \d+ contracts of this group\. Módulo does not tell them apart/,
    'Mais de uma empresa deste grupo tem contrato com este subestipulante, e o Módulo não ' +
      'diz qual. Importe essa empresa pela página dela.',
  ],
  [
    /^matches \d+ contracts of this group/,
    'Mais de um contrato deste grupo tem este subestipulante. Preencha o Módulo para dizer ' +
      'qual.',
  ],
  [
    /^matches no contract/,
    'Nenhum contrato desta empresa tem este subestipulante. Confira o código, ou cadastre o ' +
      'contrato antes.',
  ],
  [
    /^matches \d+ contracts/,
    'Mais de um contrato desta empresa tem este subestipulante. Preencha o Módulo para dizer ' +
      'qual.',
  ],
];

const REASON_FOR_ROW = 'Esta linha não foi aceita. Confira as colunas obrigatórias.';

function reasonFor(column: string | null, message: string): string {
  // Two shapes are distinctive enough to key on and worth the extra sentence:
  // a blank cell and a malformed one need different fixes, and a duplicate CPF
  // is only fixable once you know which other line it collides with.
  if (message === REQUIRED_AND_EMPTY) {
    return 'Está em branco, e esta coluna é obrigatória.';
  }
  const duplicate = DUPLICATE_OF_ROW.exec(message);
  if (duplicate !== null) {
    return `O mesmo CPF já aparece na linha ${duplicate[1]}. Um contrato tem uma linha por pessoa.`;
  }
  if (column === 'subestipulante') {
    const known = SUBESTIPULANTE_REASONS.find(([pattern]) => pattern.test(message));
    if (known !== undefined) {
      return known[1];
    }
  }
  if (column === null) {
    return REASON_FOR_ROW;
  }
  return REASON_BY_COLUMN[column] ?? REASON_FOR_ROW;
}

/** The pt-BR name of a canonical column, or the raw one if we do not know it. */
function columnLabel(column: string | null): string {
  if (column === null) {
    return '—';
  }
  return COLUMN_LABELS[column] ?? column;
}

/**
 * The offending rows of a rejected import, in the order the file has them.
 *
 * Returns an empty list for anything that is not a per-row 422 -- a format
 * refusal, a 409, a dropped connection -- so the caller can ask "is there a row
 * list to show?" by looking at the list rather than at the status code.
 */
export function rowProblemsOf(error: unknown): readonly RowProblem[] {
  if (!(error instanceof ApiError) || error.status !== 422) {
    return [];
  }
  return error.details
    .map((detail: ErrorDetail): RowProblem | null => {
      if (detail.field === null || detail.field === undefined) {
        return null;
      }
      const match = ROW_LOCATION.exec(detail.field);
      if (match === null) {
        // A validation error about the form itself (`body.baseline_date`)
        // rather than about a row of the file. Not a row problem.
        return null;
      }
      const column = match[2] ?? null;
      return {
        line: match[1] as string,
        column: columnLabel(column),
        reason: reasonFor(column, detail.message),
      };
    })
    .filter((problem): problem is RowProblem => problem !== null);
}

/** A 422 about the file as a whole: rejected, with no row to point at. */
export function isFormatRefusal(error: unknown): boolean {
  return error instanceof ApiError && error.status === 422 && rowProblemsOf(error).length === 0;
}

/** A 409: the contract has moved on, and only an explicit override gets past it. */
export function isMovedOnRefusal(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

/**
 * What the server said, verbatim, or null.
 *
 * Shown only for the two refusals whose specifics we cannot reconstruct -- see
 * the module docstring. Always beneath a label naming it as the server's words.
 */
export function serverSentence(error: unknown): string | null {
  return error instanceof ApiError ? (error.serverMessage ?? null) : null;
}
